/**
 * How the builder and the report name a priced product: its own name and its
 * price, "AvoKind Green Boost · $29.99" (owner decision A, 2026-09-25: one
 * product priced several ways), a subscription's with its billing period,
 * "Perplexity Pro · $20.00/month" in the study's content language (decisions
 * 7 and 28). Every price version keeps the product's name, so this label is
 * what tells them apart where versions are shown together.
 *
 * The one formatter. The report's `product_version_label`
 * (`canonical_deck/slides/helpers.py`) prints the same string, and
 * `tests/fixtures/questionnaire-builder/product-version-labels.golden.json`
 * pins the two to each other.
 */

import { productPriceStructureIssues } from "./accepted-artifact-validator";
import { PRODUCT_PRICE_CURRENCIES } from "./registry";
import type { ProductAssetPrice } from "./draft.generated";
import { fillRespondentWording, type RespondentWording } from "./scale-wording-packs";

/** The wording a price is printed with: the study's content language's. */
export type PriceWording = Pick<RespondentWording, "pricePeriod">;

/**
 * A price the way every renderer that draws one spells it:
 * `Intl.NumberFormat('en-US', {style: 'currency'})`, with the ordinary space
 * the report's prose uses where Intl puts a no-break space ("CHF 110.00"). An
 * unknown currency code makes Intl throw; it then reads "110 XYZ".
 */
export function formatProductPrice(
  price: Pick<ProductAssetPrice, "amount" | "currencyCode">,
): string {
  const currency = price.currencyCode.trim().toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency })
      .format(price.amount)
      .replace(/ /g, " ");
  } catch {
    return `${price.amount} ${currency}`;
  }
}

/**
 * A typed price as respondents and readers see it: the amount, with a
 * subscription's billing period in the wording's language ("$20.00/month",
 * "$20.00/mois"). `formatProductPrice` stays the amount alone.
 */
export function formatTypedPrice(
  price: ProductAssetPrice,
  wording: PriceWording,
): string {
  const amount = formatProductPrice(price);
  return price.billingPeriod
    ? fillRespondentWording(wording.pricePeriod[price.billingPeriod], { price: amount })
    : amount;
}

/**
 * The name and its price. A name that already ends with that exact formatted
 * price (a pasted offer such as "Family kit — $8.99") is left as it is, so the
 * price is never printed twice; that is a literal comparison with this
 * formatter's own output, not a reading of the name. A price the tested-asset
 * price contract refuses (`productPriceStructureIssues`, its one owner: an
 * amount above 0 in one of `PRODUCT_PRICE_CURRENCIES`), or none: the name.
 */
export function productVersionLabel(
  name: string,
  price: unknown,
  wording: PriceWording,
): string {
  const label = name.trim();
  if (price == null || productPriceStructureIssues(price).length > 0) {
    return label;
  }
  const formatted = formatTypedPrice(price as ProductAssetPrice, wording);
  return label.endsWith(formatted) ? label : `${label} · ${formatted}`;
}

/**
 * The mark the formatter writes before an amount in each currency it prints
 * ("$", "€", "CHF "): `formatProductPrice`'s own output for 1, its amount left
 * out. Derived from the one formatter, never listed by hand.
 */
const PRICE_MARKS: ReadonlyArray<string> = [
  ...new Set(
    PRODUCT_PRICE_CURRENCIES.map((currencyCode) => {
      const formatted = formatProductPrice({ amount: 1, currencyCode });
      const at = formatted.search(/\d/);
      return at > 0 ? formatted.slice(0, at).trimEnd() : "";
    }).filter((mark) => mark.length > 0),
  ),
];

/**
 * Whether `text` writes an amount the way the formatter marks a price: one of
 * its currency marks (`PRICE_MARKS`) right before a digit, a space between
 * them aside ("$6", "€ 12", "CHF 110"). A literal check of the formatter's own
 * marks, never a reading of what the text means: an amount written in words
 * ("six dollars") is no price here.
 */
export function writesFormattedPrice(text: string): boolean {
  return PRICE_MARKS.some((mark) => {
    for (let at = text.indexOf(mark); at !== -1; at = text.indexOf(mark, at + 1)) {
      const rest = text.slice(at + mark.length).trimStart();
      if (rest.length > 0 && rest[0] >= "0" && rest[0] <= "9") return true;
    }
    return false;
  });
}
